import React, { useState, useEffect } from 'react';

const SettingsPanel = ({ isOpen, onClose, onSettingsChange }) => {
  const [settings, setSettings] = useState({
    chunkSize: 4000,
    chunkOverlap: 400,
    maxChunks: 20,
    aiModel: 'gemini',
    processingMethod: 'full-pdf'
  });

  // Load settings from localStorage on component mount
  useEffect(() => {
    const savedSettings = localStorage.getItem('chunkingSettings');
    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        setSettings(parsed);
      } catch (error) {
        console.error('Failed to parse saved settings:', error);
      }
    }
  }, []);

  // Save settings to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('chunkingSettings', JSON.stringify(settings));
    // Notify parent component of settings change
    if (onSettingsChange) {
      onSettingsChange(settings);
    }
  }, [settings, onSettingsChange]);

  const handleInputChange = (field, value) => {
    if (field === 'aiModel' || field === 'processingMethod') {
      setSettings(prev => ({
        ...prev,
        [field]: value
      }));
    } else {
      const numValue = parseInt(value, 10);
      if (!isNaN(numValue) && numValue > 0) {
        setSettings(prev => ({
          ...prev,
          [field]: numValue
        }));
      }
    }
  };

  const resetToDefaults = () => {
    setSettings({
      chunkSize: 4000,
      chunkOverlap: 400,
      maxChunks: 20,
      aiModel: 'gemini',
      processingMethod: 'full-pdf'
    });
  };

  if (!isOpen) return null;

  return (
    <div className="settings-overlay">
      <div className="settings-panel">
        <div className="settings-header">
          <h3>⚙️ AI & Chunking Settings</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="settings-content">
          <p className="settings-description">
            Choose your AI model and adjust how PDF documents are split for analysis. Larger chunks provide more context but may be less precise.
          </p>
          {/* Phase 1: Claude + OpenAI only */}

          <div className="setting-group">
            <label htmlFor="aiModel">
              <span className="setting-label">AI Model</span>
              <span className="setting-description">Choose the AI model for question answering</span>
            </label>
            <select
              id="aiModel"
              value={settings.aiModel}
              onChange={(e) => handleInputChange('aiModel', e.target.value)}
            >
              <option value="claude">Claude</option>
              <option value="openai">OpenAI</option>
              <option value="gemini">Gemini</option>
              <option value="openrouter">OpenRouter</option>
            </select>
          </div>

          <div className="setting-group">
            <label htmlFor="chunkSize">
              <span className="setting-label">Chunk Size (characters)</span>
              <span className="setting-description">Size of each text chunk</span>
            </label>
            <input
              id="chunkSize"
              type="number"
              min="1000"
              max="100000"
              step="100"
              value={settings.chunkSize}
              onChange={(e) => handleInputChange('chunkSize', e.target.value)}
            />
          </div>

          <div className="setting-group">
            <label htmlFor="chunkOverlap">
              <span className="setting-label">Chunk Overlap (characters)</span>
              <span className="setting-description">Overlap between adjacent chunks</span>
            </label>
            <input
              id="chunkOverlap"
              type="number"
              min="0"
              max="1000"
              step="50"
              value={settings.chunkOverlap}
              onChange={(e) => handleInputChange('chunkOverlap', e.target.value)}
            />
          </div>

          <div className="setting-group">
            <label htmlFor="maxChunks">
              <span className="setting-label">Max Chunks Retrieved</span>
              <span className="setting-description">Maximum chunks to use for each question</span>
            </label>
            <input
              id="maxChunks"
              type="number"
              min="5"
              max="50"
              step="1"
              value={settings.maxChunks}
              onChange={(e) => handleInputChange('maxChunks', e.target.value)}
            />
          </div>

          <div className="setting-group">
            <label htmlFor="processingMethod">
              <span className="setting-label">Processing Method</span>
              <span className="setting-description">Choose how to process PDF content</span>
            </label>
            <select
              id="processingMethod"
              value={settings.processingMethod}
              onChange={(e) => handleInputChange('processingMethod', e.target.value)}
            >
              <option value="embeddings">Embeddings</option>
              <option value="full-pdf">Full-PDF</option>
            </select>
          </div>

          <div className="settings-info">
            <h4>Current Configuration:</h4>
            <ul>
              <li>Context Window: ~{Math.round(settings.chunkSize * settings.maxChunks / 1000)}K characters</li>
              <li>Estimated Tokens: ~{Math.round(settings.chunkSize * settings.maxChunks / 4)}</li>
              <li>Typical 25-page PDF: {settings.chunkSize * settings.maxChunks >= 60000 ? '✅ Full coverage' : '⚠️ Partial coverage'}</li>
            </ul>
          </div>
        </div>

        <div className="settings-actions">
          <button className="btn-secondary" onClick={resetToDefaults}>
            Reset to Defaults
          </button>
          <button className="btn-primary" onClick={onClose}>
            Apply Settings
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettingsPanel;